import { ReactNode, useCallback, useEffect, useState } from "react";
import { Check, Loader, X } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import SearchInput from "@/components/common/SearchInput";
import templateApi from "@/api/template";
import { TemplateName, TemplateActions } from "./TemplateDetails";
import { Template } from "@/interfaces/template.interface";

interface Filter {
  artworkId?: string;
  cropSettingsId?: string;
}

interface Props {
  filter?: Filter;
  header?: ReactNode;
  onChange?: (item?: Template) => void;
  onSelect?: (items: Template[]) => void;
  onRemove?: (item: Template) => void;
}

export default function TemplateList({
  filter,
  header,
  onChange,
  onSelect,
  onRemove,
}: Props) {
  const [itemList, setItemList] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState<Template[] | Template>([]);

  const onPageChange = (event: any) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const getItemList = useCallback(() => {
    if (!filter) {
      setItemList([]);
      setTotalItems(0);
      return;
    }

    setLoading(true);
    templateApi
      .getMany({
        ...filter,
        page,
        size,
        search,
        sort: "zIndex",
        view: "list",
      })
      .then((data) => {
        setItemList(data.data);
        const pagination = data.pagination;
        if (pagination) setTotalItems(pagination.totalItems);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  useEffect(() => {
    if (selected instanceof Array) {
    } else {
      onChange?.(selected);
    }
  }, [selected, onChange]);

  const onDetails = (item: Template) => {
    window.open(
      `/artworks/${item.artworkId}?templateId=${item._id}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <div className="flex h-full flex-col overflow-auto">
        <div className="flex items-center justify-between gap-4 border-b-1 border-gray-300 p-4">
          <div className="flex flex-1 items-center gap-2">
            <span className="text-md truncate font-bold">{header}</span>
          </div>
          {loading && <Loader className="animate-spin" size={20} />}
          <div className="flex items-center gap-2">
            <SearchInput
              onChange={() => setLoading(true)}
              onSearch={setSearch}
            />
          </div>
        </div>
        <DataTable
          rowHover
          className="data-table h-full overflow-auto"
          value={itemList}
          size="small"
          scrollable
          selectionMode={(onSelect ? "multiple" : "single") as any}
          selection={selected as any}
          onSelectionChange={(e: any) => setSelected(e.value)}
        >
          {onSelect && (
            <Column headerClassName="w-10" selectionMode="multiple"></Column>
          )}
          <Column
            field="name"
            header="Name"
            body={(item) => <TemplateName data={item} />}
          ></Column>
          <Column
            headerClassName="w-28 max-w-28 min-w-28"
            className="w-28 max-w-28 min-w-28"
            frozen
            alignFrozen="right"
            field="actions"
            header="Actions"
            body={(item) => (
              <TemplateActions data={item} onDetails={onDetails} />
            )}
          ></Column>
          {onRemove && (
            <Column
              headerClassName="w-32 max-w-32 min-w-32"
              className="w-32 max-w-32 min-w-32"
              style={{ borderLeft: "1px solid #e5e7eb" }}
              frozen
              alignFrozen="right"
              body={(item) => (
                <div className="flex justify-center">
                  <Button
                    severity="warning"
                    style={{
                      height: 36,
                      margin: 0,
                      padding: "6px 10px",
                      gap: 4,
                    }}
                    label="Remove"
                    icon={<X size={18} />}
                    onClick={() => onRemove(item)}
                  />
                </div>
              )}
            ></Column>
          )}
        </DataTable>
        {selected instanceof Array && selected.length > 0 && (
          <div className="flex items-center justify-end gap-4 border-t-1 border-gray-300 p-4">
            <span>{selected.length} selected</span>
            <Button
              severity="info"
              style={{
                height: 36,
                margin: 0,
                padding: "6px 10px",
                gap: 4,
              }}
              label="Select"
              icon={<Check size={18} />}
              onClick={() => onSelect?.(selected)}
            />
          </div>
        )}
        <div className="pagination flex w-full justify-end border-t-1 border-gray-300">
          <Paginator
            first={size * (page - 1)}
            rows={size}
            totalRecords={totalItems}
            // rowsPerPageOptions={[10, 20, 50]}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
}
